import { CreateDepartmentRequest, Department, DepartmentDetail, UtilityUser } from "../model/departments/department";
import { UserDepartmentsResponse } from "../model/workflows/user-department";
import { apiSlice } from "../stores/base/api-slice";

export interface RemoveStaffRequest {
	departmentId: string;
	userId: string;
}

export interface AddStaffRequest {
	departmentId: string;
	userIds: string[];
}

const departmentEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getDepartments: builder.query<Department[], void>({
			query: () => ({
				url: "/user/departments",
				method: "GET",
			}),
			providesTags: ["Departments"],
			keepUnusedDataFor: 0, // Don't cache data
		}),
		getDepartmentById: builder.query<DepartmentDetail, string>({
			query: id => ({
				url: `/user/departments/get-department-by-id/${id}`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "Departments", id }],
			keepUnusedDataFor: 0, // Don't cache data
		}),
		getUtilityUsers: builder.query<UtilityUser[], void>({
			query: () => ({
				url: "/user/users/utility-users",
				method: "GET",
			}),
			providesTags: ["UtilityUsers"],
			keepUnusedDataFor: 0, // Don't cache data
		}),
		getUtilityStaffUsers: builder.query<UtilityUser[], void>({
			query: () => ({
				url: "/user/departments/get-utility-staff-users",
				method: "GET",
			}),
			providesTags: ["UtilityStaffUsers"],
			keepUnusedDataFor: 0, // Don't cache data
		}),
		createDepartment: builder.mutation<void, CreateDepartmentRequest>({
			query: data => ({
				url: "/user/departments/create-department",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Departments"],
		}),
		deleteDepartment: builder.mutation<void, string>({
			query: id => ({
				url: `/user/departments/delete-department/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Departments"],
		}),
		addStaffToDepartment: builder.mutation<void, AddStaffRequest>({
			query: data => ({
				url: "/user/departments/add-staff-to-department",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Departments", "UtilityStaffUsers"],
		}),
		removeStaffFromDepartment: builder.mutation<void, RemoveStaffRequest>({
			query: data => ({
				url: "/user/departments/remove-staff-from-department",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Departments", "UtilityStaffUsers"],
		}),
		getUserDepartments: builder.query<UserDepartmentsResponse, void>({
			query: () => ({
				url: "/user/departments/get-user-departments",
				method: "GET",
			}),
			providesTags: ["UserDepartments"],
		}),
	}),
});

export const {
	useGetDepartmentsQuery,
	useLazyGetDepartmentsQuery,
	useGetDepartmentByIdQuery,
	useLazyGetDepartmentByIdQuery,
	useGetUtilityUsersQuery,
	useGetUtilityStaffUsersQuery,
	useCreateDepartmentMutation,
	useDeleteDepartmentMutation,
	useAddStaffToDepartmentMutation,
	useRemoveStaffFromDepartmentMutation,
	useGetUserDepartmentsQuery,
} = departmentEndpoints;
